import axios from 'axios';
import { API_BASE_URL } from './config';
import { partnerService } from './partnerService';

class SaasService {
    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true
        });
    }

    // Hilfsmethode zur Anwendung des Partner-Rabatts
    applyPartnerDiscount(price, isPartner, productType) {
        // Kein Rabatt für Microsoft M365
        if (productType === 'office-365') {
            return { 
                finalPrice: price,
                originalPrice: null, // Kein Rabatt, daher kein Original-Preis
                discountApplied: false
            };
        }

        // 20% Rabatt für Partner bei allen anderen Produkten
        if (isPartner) {
            const discountedPrice = price * 0.8; // 20% Rabatt
            return {
                finalPrice: parseFloat(discountedPrice.toFixed(2)),
                originalPrice: parseFloat(price.toFixed(2)),
                discountApplied: true
            };
        }

        // Kein Rabatt für Nicht-Partner
        return {
            finalPrice: price,
            originalPrice: null,
            discountApplied: false
        };
    }

    // Preisberechnung für verschiedene Produkte
    calculateBackupNowPrice(config, isPartner = false) {
        const basePrice = 5; // Basispreis für Backup Now
        return this.applyPartnerDiscount(basePrice, isPartner, 'backup-now');
    }

    calculateOffice365Price(config, isPartner = false) {
        // Sicherstellen dass die Anzahl der Lizenzen eine gültige Zahl ist
        const licenses = typeof config.licenses === 'number' ? config.licenses : 0;
        if (licenses <= 0) {
            console.warn('Ungültige Anzahl an Lizenzen für Office 365. Mindestens 1 Lizenz erforderlich.');
            return { finalPrice: 0, originalPrice: null, discountApplied: false }; // 0 wird vom Server als ungültig erkannt
        }
        const basePrice = licenses * 12.50; // 12.50€ pro Lizenz
        return this.applyPartnerDiscount(basePrice, isPartner, 'office-365');
    }

    calculateTSPlusPrice(config, isPartner = false) {
        // Sicherstellen dass die Anzahl der Benutzer eine gültige Zahl ist
        const users = typeof config.users === 'number' ? config.users : 0;
        if (users <= 0) {
            console.warn('Ungültige Anzahl an Benutzern für TSPlus. Mindestens 1 Benutzer erforderlich.');
            return { finalPrice: 0, originalPrice: null, discountApplied: false };
        }
        const basePrice = users * 8.50; // 8.50€ pro User
        return this.applyPartnerDiscount(basePrice, isPartner, 'tsplus');
    }

    calculateESETPrice(config, isPartner = false) {
        // Sicherstellen dass die Anzahl der Lizenzen eine gültige Zahl ist
        const licenses = typeof config.licenses === 'number' ? config.licenses : 0;
        if (licenses <= 0) {
            console.warn('Ungültige Anzahl an Lizenzen für ESET. Mindestens 1 Lizenz erforderlich.');
            return { finalPrice: 0, originalPrice: null, discountApplied: false };
        }
        const basePrice = licenses * 3; // 3€ pro Lizenz
        return this.applyPartnerDiscount(basePrice, isPartner, 'eset');
    }

    calculateLastpassPrice(config, isPartner = false) {
        // Sicherstellen dass die Anzahl der Benutzer eine gültige Zahl ist
        const users = typeof config.users === 'number' ? config.users : 0;
        if (users <= 0) {
            console.warn('Ungültige Anzahl an Benutzern für Lastpass. Mindestens 1 Benutzer erforderlich.');
            return { finalPrice: 0, originalPrice: null, discountApplied: false };
        }
        const basePrice = users * 4; // 4€ pro User
        return this.applyPartnerDiscount(basePrice, isPartner, 'lastpass');
    }

    calculateNextcloudPrice(config, isPartner = false) {
        let basePrice = 0;
        // Sicherstellen dass die Parameter gültige Zahlen sind
        const storage = typeof config.storage === 'number' ? config.storage : 0;
        const users = typeof config.users === 'number' ? config.users : 0;
        
        if (users <= 0) {
            console.warn('Ungültige Anzahl an Benutzern für Nextcloud. Mindestens 1 Benutzer erforderlich.');
            return { finalPrice: 0, originalPrice: null, discountApplied: false };
        }

        basePrice += storage * 0.3; // 0.30€ pro GB
        basePrice += users * 5; // 5€ pro User
        return this.applyPartnerDiscount(basePrice, isPartner, 'nextcloud');
    }

    // Service Bestellung
    async orderService(productType, configuration, endCustomerPrice) {
        let priceInfo = { finalPrice: 0, originalPrice: null, discountApplied: false };

        try {
            // Partner-Status abrufen
            const isPartner = await partnerService.isPartner();

            // Preisberechnung je nach Produkttyp
            switch(productType) {
                case 'backup-now':
                    priceInfo = this.calculateBackupNowPrice(configuration, isPartner);
                    break;
                case 'office-365':
                    priceInfo = this.calculateOffice365Price(configuration, isPartner);
                    break;
                case 'tsplus':
                    priceInfo = this.calculateTSPlusPrice(configuration, isPartner);
                    break;
                case 'eset':
                    priceInfo = this.calculateESETPrice(configuration, isPartner);
                    break;
                case 'lastpass':
                    priceInfo = this.calculateLastpassPrice(configuration, isPartner);
                    break;
                case 'nextcloud':
                    priceInfo = this.calculateNextcloudPrice(configuration, isPartner);
                    break;
                default:
                    console.error('Unbekannter Produkttyp:', productType);
                    throw new Error(`Unbekannter Produkttyp: ${productType}`);
            }

            // Preis validieren
            if (typeof priceInfo.finalPrice !== 'number' || isNaN(priceInfo.finalPrice) || priceInfo.finalPrice <= 0) {
                console.error('Ungültiger Preis berechnet:', priceInfo.finalPrice);
                throw new Error(`Ungültiger Preis: ${priceInfo.finalPrice}`);
            }

            // Zusätzliche Informationen zum Rabatt in die Konfiguration einfügen
            if (priceInfo.discountApplied) {
                configuration.priceInfo = {
                    originalPrice: priceInfo.originalPrice,
                    discountApplied: true
                };
            }

            return this.axiosInstance.post('/saas-service/order', {
                productType,
                configuration,
                totalPrice: priceInfo.finalPrice,
                ...(endCustomerPrice && parseFloat(endCustomerPrice) > 0
                    ? { endCustomerPrice: parseFloat(endCustomerPrice) }
                    : {})
            });
        } catch (error) {
            console.error('Fehler bei der SaaS-Bestellung:', error);
            throw error;
        }
    }

    // Aktive Services eines Benutzers abrufen
    async getActiveUserServices() {
        return this.axiosInstance.get('/saas-service/active');
    }

    // Alle Services eines Benutzers abrufen
    async getUserServices() {
        return this.axiosInstance.get('/saas-service');
    }
    
    // Service einem Kunden zuweisen
    async assignServiceToCustomer(serviceId, customerId, endCustomerPrice) {
        try {
            const body = { customerId };
            if (endCustomerPrice && parseFloat(endCustomerPrice) > 0) {
                body.endCustomerPrice = parseFloat(endCustomerPrice);
            }
            const response = await this.axiosInstance.put(
                `/saas-service/assign/${serviceId}`,
                body
            );
            return response.data;
        } catch (error) {
            console.error('Fehler beim Zuweisen des Services:', error);
            throw error;
        }
    }

    // Endkundenpreis eines bestehenden Services setzen/ändern
    async updateEndCustomerPrice(serviceId, price) {
        return this.axiosInstance.put(
            `/saas-service/${serviceId}/endcustomer-price`,
            { endCustomerPrice: price !== '' ? price : null }
        );
    }
}

export default new SaasService();